import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Language, getTranslation } from '@/lib/translations';

// Exchange rate (approximate, can be updated periodically)
const CRC_TO_USD_RATE = 0.0019; // 1 CRC ≈ 0.0019 USD

interface LocaleContextType {
  country: string;
  language: Language;
  isUSVisitor: boolean;
  isLoading: boolean;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  formatPrice: (amountCRC: number, forceCurrency?: 'CRC' | 'USD') => string;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

const COUNTRY_CACHE_KEY = 'ticomarket_visitor_country';
const LANGUAGE_CACHE_KEY = 'ticomarket_language';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

interface CachedCountry {
  country: string;
  timestamp: number;
}

export const LocaleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [country, setCountry] = useState<string>('CR');
  const [language, setLanguageState] = useState<Language>('es');
  const [isLoading, setIsLoading] = useState(true);

  // Detect country on mount
  useEffect(() => {
    const detectCountry = async () => {
      try {
        // Check cache first
        const cached = localStorage.getItem(COUNTRY_CACHE_KEY);
        if (cached) {
          const parsed: CachedCountry = JSON.parse(cached);
          if (Date.now() - parsed.timestamp < CACHE_DURATION) {
            setCountry(parsed.country);
            // Set language based on country if not explicitly set
            const savedLang = localStorage.getItem(LANGUAGE_CACHE_KEY);
            if (!savedLang) {
              setLanguageState(parsed.country === 'US' ? 'en' : 'es');
            }
            setIsLoading(false);
            return;
          }
        }

        // Call edge function to detect country
        const { data, error } = await supabase.functions.invoke('get-visitor-country');
        
        if (error) {
          console.error('Error detecting country:', error);
          setIsLoading(false);
          return;
        }

        const detectedCountry = data?.country || 'CR';
        setCountry(detectedCountry);
        
        // Cache the result
        localStorage.setItem(COUNTRY_CACHE_KEY, JSON.stringify({
          country: detectedCountry,
          timestamp: Date.now()
        }));

        // Set default language based on country
        const savedLang = localStorage.getItem(LANGUAGE_CACHE_KEY);
        if (!savedLang) {
          const defaultLang = detectedCountry === 'US' ? 'en' : 'es';
          setLanguageState(defaultLang);
          localStorage.setItem(LANGUAGE_CACHE_KEY, defaultLang);
        }
      } catch (error) {
        console.error('Error in country detection:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Load cached language preference first
    const savedLang = localStorage.getItem(LANGUAGE_CACHE_KEY) as Language | null;
    if (savedLang && (savedLang === 'es' || savedLang === 'en')) {
      setLanguageState(savedLang);
    }

    detectCountry();
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(LANGUAGE_CACHE_KEY, lang);
  }, []);

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    return getTranslation(language, key, params);
  }, [language]);

  const formatPrice = useCallback((amountCRC: number, forceCurrency?: 'CRC' | 'USD'): string => {
    const currency = forceCurrency || (country === 'US' ? 'USD' : 'CRC');
    
    if (currency === 'USD') {
      const amountUSD = amountCRC * CRC_TO_USD_RATE;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amountUSD);
    }
    
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amountCRC);
  }, [country]);

  const isUSVisitor = useMemo(() => country === 'US', [country]);

  const value = useMemo(() => ({
    country,
    language,
    isUSVisitor,
    isLoading,
    setLanguage,
    t,
    formatPrice
  }), [country, language, isUSVisitor, isLoading, setLanguage, t, formatPrice]);

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = (): LocaleContextType => {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

// Hook for components that need translation but might be outside provider during SSR
export const useTranslation = () => {
  const context = useContext(LocaleContext);
  
  // Fallback for components that might render before provider is ready
  if (!context) {
    return {
      t: (key: string) => getTranslation('es', key),
      language: 'es' as Language
    };
  }
  
  return {
    t: context.t,
    language: context.language
  };
};
